import { graphql, useStaticQuery } from 'gatsby'

type Style = {
  desktop: Record<string, unknown> | null | undefined
  mobile: Record<string, unknown> | null | undefined
}

type Styles = {
  imgCard: Style
  imgPctNavigationItemsFeatured: Style
  imgHighlightBasic: Style
  imgHighlightFullWidth: Style
  imgFeaturedMultipleContent: Style
  imgFeaturedMultipleContentFeatured: Style
  imgHeaderBackgroundV3?: Style
  imgHeaderEpisode?: Style
  imgInterviewsSection?: Style
}

type TypeName =
  | 'DatoCmsPctDialog'
  | 'DatoCmsPctEpisode'
  | 'DatoCmsPctPage'
  | 'DatoCmsPctPost'
  | 'DatoCmsPctInterview'
  | 'DatoCmsPctPaper'
  | 'DatoCmsPctProject'
  | 'DatoCmsPctInternalPage'
  | 'DatoCmsPctTopic'
  | 'DatoCmsPctExternalContent'
  | 'DatoCmsPctEvent'

type TypeNameIndex =
  | 'datoCmsPctDialog'
  | 'datoCmsPctEpisode'
  | 'datoCmsPctPage'
  | 'datoCmsPctPost'
  | 'datoCmsPctInterview'
  | 'datoCmsPctPaper'
  | 'datoCmsPctProject'
  | 'datoCmsPctInternalPage'
  | 'datoCmsPctTopic'
  | 'datoCmsPctExternalContent'
  | 'datoCmsPctEvent'

type DefaultImages = Record<TypeNameIndex, Styles>

export const typeNameMapping: Record<TypeName, TypeNameIndex> = {
  DatoCmsPctDialog: 'datoCmsPctDialog',
  DatoCmsPctEpisode: 'datoCmsPctEpisode',
  DatoCmsPctPage: 'datoCmsPctPage',
  DatoCmsPctPost: 'datoCmsPctPost',
  DatoCmsPctInterview: 'datoCmsPctInterview',
  DatoCmsPctPaper: 'datoCmsPctPaper',
  DatoCmsPctProject: 'datoCmsPctProject',
  DatoCmsPctInternalPage: 'datoCmsPctInternalPage',
  DatoCmsPctTopic: 'datoCmsPctTopic',
  DatoCmsPctExternalContent: 'datoCmsPctExternalContent',
  DatoCmsPctEvent: 'datoCmsPctEvent'
}

/**
 * Custom hook to retrieve default card images based on the provided type name.
 * @param typeName - The type name to determine the default card images.
 * @returns The default card images for the specified type.
 */
export default function useDefaultImages(typeName?: TypeName): Styles {
  const all = _allImages()

  if (!typeName)
    return {
      imgCard: { desktop: null, mobile: null },
      imgHighlightBasic: { desktop: null, mobile: null },
      imgHighlightFullWidth: { desktop: null, mobile: null },
      imgPctNavigationItemsFeatured: { desktop: null, mobile: null },
      imgFeaturedMultipleContent: { desktop: null, mobile: null },
      imgFeaturedMultipleContentFeatured: { desktop: null, mobile: null }
    }
  return all[typeNameMapping[typeName]]
}

export function useDefaultAllImages(): DefaultImages {
  return _allImages()
}

function _allImages(): DefaultImages {
  const data: Queries.DefaultImagesQuery = useStaticQuery(graphql`
    query DefaultImages {
      imgHeaderEpisode: datoCmsAsset(
        originalId: { eq: "O2uXPcs7TseXHVS2Q_qW_A" }
      ) {
        ...assetHeaderEpisode
      }
      imgInterviewsSection: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        gatsbyImageData(
          imgixParams: { fit: "crop", h: "325", w: "1250", crop: "bottom" }
        )
        mobile: gatsbyImageData(height: 325)
      }

      imgCard: datoCmsAsset(originalId: { eq: "PxTU38QjQMm7hIKU27TkEA" }) {
        ...assetCard
      }
      imgCardEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetCard
      }
      imgCardInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetCard
      }

      imgHighlightBasic: datoCmsAsset(
        originalId: { eq: "PxTU38QjQMm7hIKU27TkEA" }
      ) {
        ...assetHighlightBasic
      }
      imgHighlightBasicEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetHighlightBasic
      }

      imgHighlightBasicInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetHighlightBasic
      }

      imgHighlightFullWidth: datoCmsAsset(originalId: { eq: "10652724" }) {
        ...assetHighlightFullWidth
      }
      imgHighlightFullWidthEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetHighlightFullWidth
      }
      imgHighlightFullWidthInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetHighlightFullWidth
      }

      imgFeaturedMultipleContent: datoCmsAsset(
        originalId: { eq: "PxTU38QjQMm7hIKU27TkEA" }
      ) {
        ...assetFeaturedMultipleContent
      }
      imgFeaturedMultipleContentEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetFeaturedMultipleContent
      }
      imgFeaturedMultipleContentInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetFeaturedMultipleContent
      }
      imgFeaturedMultipleContentFeatured: datoCmsAsset(
        originalId: { eq: "PxTU38QjQMm7hIKU27TkEA" }
      ) {
        ...assetFeaturedMultipleContentFeatured
      }
      imgFeaturedMultipleContentFeaturedEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetFeaturedMultipleContentFeatured
      }
      imgFeaturedMultipleContentFeaturedInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetFeaturedMultipleContentFeatured
      }
      imgPctNavigationItemsFeatured: datoCmsAsset(
        originalId: { eq: "PxTU38QjQMm7hIKU27TkEA" }
      ) {
        ...assetPctNavigationItemsFeatured
      }
      imgPctNavigationItemsFeaturedEpisode: datoCmsAsset(
        originalId: { eq: "MEKjR49JQp-yGWzKY_jxeg" }
      ) {
        ...assetPctNavigationItemsFeatured
      }
      imgPctNavigationItemsFeaturedInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetPctNavigationItemsFeatured
      }
      imgPctNavigationItemsFeaturedInterview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetPctNavigationItemsFeatured
      }

      imgHeaderBackgroundV3Interview: datoCmsAsset(
        originalId: { eq: "Z8vgWiiFS6ioHOf9zSn-qw" }
      ) {
        ...assetHeaderBackgroundV3
      }
    }
  `)

  const {
    imgCard,
    imgCardEpisode,
    imgCardInterview,
    imgPctNavigationItemsFeatured,
    imgPctNavigationItemsFeaturedInterview,
    imgPctNavigationItemsFeaturedEpisode,
    imgFeaturedMultipleContent,
    imgFeaturedMultipleContentEpisode,
    imgFeaturedMultipleContentInterview,
    imgFeaturedMultipleContentFeatured,
    imgFeaturedMultipleContentFeaturedEpisode,
    imgFeaturedMultipleContentFeaturedInterview,
    imgHighlightBasic,
    imgHighlightBasicInterview,
    imgHighlightBasicEpisode,
    imgHighlightFullWidth,
    imgHighlightFullWidthEpisode,
    imgHighlightFullWidthInterview,
    imgHeaderBackgroundV3Interview,
    imgHeaderEpisode,
    imgInterviewsSection
  } = data

  const defaultStyles: Styles = {
    imgCard: {
      desktop: imgCard?.gatsbyImageData,
      mobile: imgCard?.gatsbyImageData
    },
    imgPctNavigationItemsFeatured: {
      desktop: imgPctNavigationItemsFeatured?.gatsbyImageData,
      mobile: imgPctNavigationItemsFeatured?.gatsbyImageData
    },
    imgHighlightBasic: {
      desktop: imgHighlightBasic?.gatsbyImageData,
      mobile: imgHighlightBasic?.gatsbyImageData
    },
    imgHighlightFullWidth: {
      desktop: imgHighlightFullWidth?.gatsbyImageData,
      mobile: imgHighlightFullWidth?.mobile
    },
    imgFeaturedMultipleContent: {
      desktop: imgFeaturedMultipleContent?.gatsbyImageData,
      mobile: imgFeaturedMultipleContent?.mobile
    },
    imgFeaturedMultipleContentFeatured: {
      desktop: imgFeaturedMultipleContentFeatured?.gatsbyImageData,
      mobile: imgFeaturedMultipleContentFeatured?.mobile
    }
  }

  const all: DefaultImages = {
    datoCmsPctEpisode: {
      imgCard: {
        desktop: imgCardEpisode?.gatsbyImageData,
        mobile: imgCardEpisode?.gatsbyImageData
      },
      imgHighlightBasic: {
        desktop: imgHighlightBasicEpisode?.gatsbyImageData,
        mobile: imgHighlightBasicEpisode?.gatsbyImageData
      },
      imgHighlightFullWidth: {
        desktop: imgHighlightFullWidthEpisode?.gatsbyImageData,
        mobile: imgHighlightFullWidthEpisode?.mobile
      },
      imgPctNavigationItemsFeatured: {
        desktop: imgPctNavigationItemsFeaturedEpisode?.gatsbyImageData,
        mobile: imgPctNavigationItemsFeaturedEpisode?.gatsbyImageData
      },
      imgFeaturedMultipleContent: {
        desktop: imgFeaturedMultipleContentEpisode?.gatsbyImageData,
        mobile: imgFeaturedMultipleContentEpisode?.mobile
      },
      imgFeaturedMultipleContentFeatured: {
        desktop: imgFeaturedMultipleContentFeaturedEpisode?.gatsbyImageData,
        mobile: imgFeaturedMultipleContentFeaturedEpisode?.mobile
      },
      imgHeaderEpisode: {
        desktop: imgHeaderEpisode?.gatsbyImageData,
        mobile: imgHeaderEpisode?.gatsbyImageData
      }
    },
    datoCmsPctDialog: defaultStyles,
    datoCmsPctPage: defaultStyles,
    datoCmsPctPaper: defaultStyles,
    datoCmsPctPost: defaultStyles,
    datoCmsPctEvent: defaultStyles,
    datoCmsPctExternalContent: defaultStyles,
    datoCmsPctInterview: {
      imgCard: {
        desktop: imgCardInterview?.gatsbyImageData,
        mobile: imgCardInterview?.gatsbyImageData
      },
      imgHeaderBackgroundV3: {
        desktop: imgHeaderBackgroundV3Interview?.gatsbyImageData,
        mobile: imgHeaderBackgroundV3Interview?.mobile
      },
      imgHighlightBasic: {
        desktop: imgHighlightBasicInterview?.gatsbyImageData,
        mobile: imgHighlightBasicInterview?.gatsbyImageData
      },
      imgHighlightFullWidth: {
        desktop: imgHighlightFullWidthInterview?.gatsbyImageData,
        mobile: imgHighlightFullWidthInterview?.mobile
      },
      imgPctNavigationItemsFeatured: {
        desktop: imgPctNavigationItemsFeaturedInterview?.gatsbyImageData,
        mobile: imgPctNavigationItemsFeaturedInterview?.gatsbyImageData
      },
      imgFeaturedMultipleContent: {
        desktop: imgFeaturedMultipleContentInterview?.gatsbyImageData,
        mobile: imgFeaturedMultipleContentInterview?.mobile
      },
      imgFeaturedMultipleContentFeatured: {
        desktop: imgFeaturedMultipleContentFeaturedInterview?.gatsbyImageData,
        mobile: imgFeaturedMultipleContentFeaturedInterview?.mobile
      },
      imgInterviewsSection: {
        desktop: imgInterviewsSection?.gatsbyImageData,
        mobile: imgInterviewsSection?.mobile
      }
    },
    datoCmsPctProject: defaultStyles,
    datoCmsPctInternalPage: defaultStyles,
    datoCmsPctTopic: defaultStyles
  }

  return all
}
